import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}
function validerFormats(e) {
  let totalBouteilles = 0;
  let totalMagnums = 0;

  document.querySelectorAll('dt').forEach(dt => {
    if (dt.textContent.trim() === 'Format:') {
      const format = dt.nextElementSibling?.textContent?.trim();
      const cartRow = dt.closest('tr');
      const qtyInput = cartRow?.querySelector('input[name="updates[]"]');
      const qty = parseInt(qtyInput?.value || 0);
      if (format === 'Bouteille') totalBouteilles += qty;
      if (format === 'Magnum') totalMagnums += qty;
    }
  });

  const invalid =
    (totalBouteilles > 0 && totalBouteilles % 6 !== 0) ||
    (totalMagnums > 0 && totalMagnums % 3 !== 0);

  // Supprimer ancien message
  document.querySelector('#cart-validation-message')?.remove();

  if (invalid) {
    e.preventDefault();
    e.stopPropagation();

    const msg = document.createElement('div');
    msg.id = 'cart-validation-message';
    msg.style.cssText = 'background:#f8f0e8;border:none;padding:16px;margin:16px 0;font-size:13px;line-height:1.6;color:#333;';
    const manqueBouteilles = totalBouteilles > 0 && totalBouteilles % 6 !== 0 ? 6 - (totalBouteilles % 6) : 0;
    const manqueMagnums = totalMagnums > 0 && totalMagnums % 3 !== 0 ? 3 - (totalMagnums % 3) : 0;
    let lignes = '';
    if (manqueBouteilles > 0) lignes += `Votre commande comprend ${totalBouteilles} bouteille${totalBouteilles > 1 ? 's' : ''}, il vous manque ${manqueBouteilles} bouteille${manqueBouteilles > 1 ? 's' : ''} pour compléter un carton de 6.<br>`;
    if (manqueMagnums > 0) lignes += `Votre commande comprend ${totalMagnums} magnum${totalMagnums > 1 ? 's' : ''}, il vous manque ${manqueMagnums} magnum${manqueMagnums > 1 ? 's' : ''} pour compléter un carton de 3.<br>`;
    msg.innerHTML = lignes + '<br>Les clavelins de Vin Jaune sont vendus \u00e0 l\u2019unit\u00e9, en caisse bois.';

    const checkoutBtn = document.querySelector('#checkout');
    checkoutBtn.parentNode.insertBefore(msg, checkoutBtn);
    msg.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

function attachCheckoutValidation() {
  const btn = document.querySelector('#checkout');
  if (btn && !btn.dataset.validationAttached) {
    btn.dataset.validationAttached = 'true';
    btn.addEventListener('click', validerFormats);
  }
}

const observer = new MutationObserver(attachCheckoutValidation);
observer.observe(document.body, { childList: true, subtree: true });
attachCheckoutValidation();

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
